import { useLazyQuery } from '@apollo/client';
import { Box, DialogActions, styled } from '@mui/material';
import { mergeDefaults } from 'utils/form.utils';
import { StyledDialogContent } from 'FeatureModules/ProductPage/Components/Create/styles';
import { InlineHelperText } from 'FeatureModules/ProductPage/Components/InlineHelperText/InlineHelperText';
import useDialog from 'hooks/useDialog';
import { siteClusterSchema } from 'FeatureModules/Site/schemas/sites.schema';
import { DialogDefault, ThemeButton } from 'DesignSystem';
import { Form, Formik, FormikProps } from 'formik';
import {
  ICreateSiteInput,
  IUpdateSiteInput,
} from 'graphql/secure/mutations/types/site-mutation.types';
import { IS_EXTERNAL_ID_AVAILABLE } from 'graphql/secure/queries/company.queries';
import isEqual from 'lodash/isEqual';
import React, { FC, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IDefaultDialogProps } from 'types/dialog.types';
import { ImageVariant } from 'FeatureModules/Media/types/media.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { ESiteType, IBaseClusterSite, IBaseSite } from 'FeatureModules/Site/types/site.types';
import { ICoordinates } from 'types/map.types';
import { difference } from 'utils/object.utils';
import SiteClusterForm from '../Forms/SiteClusterForm';
import useSiteMutations from '../../hooks/useSiteMutations';

interface IAddEditSiteClusterDialogProps extends IDefaultDialogProps {
  /** If passed its editing the existing site cluster */
  siteCluster?: IBaseClusterSite;
  /**
   * Hide partner selector
   */
  hidePartnerSelector?: boolean;
  /**
   * Always assigns the site to this partner
   */
  ownedBy?: IBasePartnerCompany;
  /**
   * Callback function that is called when a site is created
   */
  onSiteClusterCreated?: (site: IBaseSite) => void;
}

export interface IFormValues {
  title: string;
  image?: ImageVariant;
  ownedBy?: IBasePartnerCompany;
  locationCoordinates: ICoordinates;
  locationName: string;
  mapboxId: string;
  externalId?: string;
}

const TopBackground = styled('div')(({ theme }) => ({
  '&::before': {
    content: '""',
    position: 'absolute',
    zIndex: 0,
    top: 0,
    left: 0,
    right: 0,
    height: '42%',
    background: theme.custom.colors.backgroundLight,
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(3, 5, 4),
  justifyContent: 'space-between',
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
}));

const AddEditSiteClusterDialog: FC<IAddEditSiteClusterDialogProps> = ({
  open,
  siteCluster,
  ownedBy,
  hidePartnerSelector,
  onClose,
  onSiteClusterCreated,
}) => {
  const navigate = useNavigate();
  const { createSite, updateSite } = useSiteMutations();
  const { openDialog } = useDialog();

  const [isExternalIdAvailable, { data, variables, loading: isExternalIdAvailableLoading }] =
    useLazyQuery<{
      isExternalIdAvailable: boolean;
    }>(IS_EXTERNAL_ID_AVAILABLE, {
      variables: {
        model: 'Site',
      },
    });

  const isEdit = siteCluster !== undefined;

  const formRef = useRef<FormikProps<IFormValues>>(null);
  const initialValues = mergeDefaults(siteClusterSchema.default(), {
    title: siteCluster?.title,
    image: siteCluster?.image,
    ownedBy: ownedBy || siteCluster?.ownedBy,
    locationCoordinates: siteCluster?.locationCoordinates,
    externalId: siteCluster?.externalId,
    locationName: siteCluster?.locationName,
    mapboxId: '',
  });

  const handleSubmit = async (values: IFormValues) => {
    if (!isEdit) {
      const input: ICreateSiteInput = {
        siteType: ESiteType.CLUSTER,
        title: values.title,
        image: values.image?.id ? { imageId: values.image.id } : null,
        ownedById: ownedBy?.id || values.ownedBy?.id,
        mapboxId: values.mapboxId,
        siteClusterInput: { siteIds: [] },
        externalId: values.externalId || null,
      };
      const siteClusterCreationResponse = await createSite({ variables: { input } });
      if (siteClusterCreationResponse.data?.createSite.site) {
        const siteCluster = siteClusterCreationResponse.data.createSite.site;
        if (onSiteClusterCreated) {
          onSiteClusterCreated?.(siteCluster);
        } else {
          navigate(`/sites/${siteCluster.id}`);
        }
      }
    } else {
      const changedKeys = difference(initialValues, values);
      if (changedKeys.length) {
        const input: IUpdateSiteInput = {
          siteType: ESiteType.CLUSTER,
          externalId: values.externalId || null,
          ...(changedKeys.includes('title') ? { title: values.title } : {}),
          ...(changedKeys.includes('image')
            ? values?.image?.id
              ? { image: { imageId: values.image.id } }
              : { image: null }
            : {}),
          ...(changedKeys.includes('ownedBy')
            ? { ownedById: ownedBy?.id || values.ownedBy?.id || null }
            : {}),
          ...(changedKeys.includes('locationName') ? { mapboxId: values.mapboxId } : {}),
        };
        await updateSite({ variables: { id: siteCluster.id, input } });
      }
    }
    onClose?.();
  };

  // If there are unsaved changes, ask the user if they want to leave the page
  const handleClose = () => {
    if (formRef.current && !isEqual(formRef.current.values, initialValues)) {
      openDialog({
        type: 'ALERT',
        props: {
          title: 'Unsaved changes',
          text: 'Are you sure you want to close this window? All unsaved changes will be lost and you will not be able to undo this action.',
          itemTitle: formRef.current?.values?.title,
          submitText: 'Discard changes',
          displayCloseButton: true,
          onSubmit: onClose,
          onCancel: () => undefined,
        },
      });
    } else {
      onClose?.();
    }
  };

  /**
   * Ideally we would debounce this function, but for some reason this messes up the state is messed up and always one step behind
   */
  const customExternalIdValidation = useCallback(async (values: IFormValues) => {
    if (
      values.externalId === undefined ||
      (initialValues.externalId === values.externalId &&
        initialValues.ownedBy?.id === values.ownedBy?.id)
    ) {
      // if the value is empty or the same as the initial value, do not check availability and clear errors
      return undefined;
    }
    // Check if the query is different from the previous one and use the existing value
    if (
      !!data &&
      !!variables &&
      variables.externalId === values.externalId &&
      variables.filters.ownedById === values.ownedBy?.id
    ) {
      return data.isExternalIdAvailable === true
        ? undefined
        : { externalId: 'The ID already exists. Enter a unique ID.' };
    }
    try {
      const response = await isExternalIdAvailable({
        variables: {
          externalId: values.externalId,
          filters: {
            ownedById: values.ownedBy?.id,
          },
        },
      });
      return response.data?.isExternalIdAvailable === true
        ? undefined
        : { externalId: 'The ID already exists. Enter a unique ID.' };
    } catch (error) {
      return { externalId: 'Error checking id availability' };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DialogDefault
      title={!siteCluster ? 'Add new cluster' : 'Edit cluster'}
      onClose={handleClose}
      data-testid="add-edit-cluster-dialog"
      open={open}
      fullWidth
      iconName={!siteCluster ? 'plus' : 'edit'}
      maxWidth="md"
    >
      <TopBackground />

      <Box zIndex={2}>
        <Formik<IFormValues>
          innerRef={formRef}
          initialValues={initialValues}
          enableReinitialize
          validateOnChange
          isInitialValid={false}
          validationSchema={siteClusterSchema}
          validate={customExternalIdValidation}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, isValid }) => (
            <Form>
              <StyledDialogContent>
                <Box display="flex" alignItems="center" mb={2} position="relative">
                  <InlineHelperText
                    variant="INFO"
                    helperText="A cluster is a group of sites of the same type. A farm cluster must output the same component."
                  />
                </Box>
                <SiteClusterForm hidePartnerSelector={hidePartnerSelector} />
              </StyledDialogContent>

              <Box mt={7} />
              <StyledDialogActions>
                <ThemeButton color="BLUE_ICE" size="large" onClick={handleClose}>
                  Cancel
                </ThemeButton>
                <ThemeButton
                  loading={isSubmitting || isExternalIdAvailableLoading}
                  disabled={!isValid}
                  color="PRIMARY"
                  size="large"
                  type="submit"
                  data-testid="submit-cluster-form-btn"
                >
                  Save
                </ThemeButton>
              </StyledDialogActions>
            </Form>
          )}
        </Formik>
      </Box>
    </DialogDefault>
  );
};

export default AddEditSiteClusterDialog;
